export interface IDownload {
  url: string;
  start: (signal?: AbortSignal) => Promise<File>;
}

export class OfficeDownload implements IDownload {
  private _officeContext: Office.Context;
  private static readonly fallbackFileName = "document.docx";

  constructor(officeContext: Office.Context) {
    this._officeContext = officeContext;
  }

  get url() {
    return this._officeContext.document.url;
  }

  get fileName() {
    const url = this.url?.trim();
    if (!url) return OfficeDownload.fallbackFileName;

    const path = url.split(/[?#]/)[0];
    const name = decodeURIComponent(path.split(/[\\/]/).pop() ?? "").trim();

    return name || OfficeDownload.fallbackFileName;
  }

  start: (signal?: AbortSignal) => Promise<File> = async (signal) => {
    console.group("Download");

    if (signal?.aborted) {
      console.log("Download aborted before starting");
      console.groupEnd();
      throw new DOMException("Download aborted", "AbortError");
    }

    console.log("Downloading the file meta data");
    const chunks: Uint8Array[] = [];
    const file = await this.getFile();
    try {
      for (let i = 0; i < file.sliceCount; i++) {
        if (signal?.aborted) {
          console.log("Download aborted at chunk", i + 1);
          throw new DOMException("Download aborted", "AbortError");
        }
        console.log("Downloading the chunk ", i + 1, "/", file.sliceCount);
        const slice = await this.getSlice(file, i);
        chunks.push(new Uint8Array(slice.data));
      }
      const blob = new Blob(chunks as BlobPart[], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      return new File([blob], this.fileName, { type: blob.type });
    } finally {
      console.log("Releasing file resource from office api");
      await this.closeFile(file);
      console.groupEnd();
    }
  };

  private getFile() {
    return new Promise<Office.File>((resolve, reject) => {
      this._officeContext.document.getFileAsync(
        Office.FileType.Compressed,
        (result: Office.AsyncResult<Office.File>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(result.error);
          }
        }
      );
    });
  }

  private getSlice(file: Office.File, i: number) {
    return new Promise<Office.Slice>((resolve, reject) => {
      file.getSliceAsync(i, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      });
    });
  }

  private closeFile(file: Office.File) {
    return new Promise<void>((resolve, reject) => {
      file.closeAsync((result: Office.AsyncResult<any>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(result.error);
        }
      });
    });
  }
}
